import { useLayoutEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import axios from 'axios';
import { useAuthStore } from '../store/auth.ts';
import { dv } from '../i18n/dv.ts';

export function Login(): JSX.Element {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // Login is always shown in light theme regardless of the user's dark-mode
  // preference; their real theme is restored when navigating away.
  useLayoutEffect(() => {
    const html = document.documentElement;
    html.classList.remove('dark');
    return () => {
      try {
        const stored = localStorage.getItem('wapmeet.theme');
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        if (stored === 'dark' || (!stored && prefersDark)) html.classList.add('dark');
      } catch {
        /* ignore */
      }
    };
  }, []);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await login(email, password);
      await navigate({ to: '/' });
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 429) {
        setError(dv.common.error);
      } else if (axios.isAxiosError(e) && e.response?.status === 401) {
        setError(dv.auth.invalidCredentials);
      } else {
        setError(dv.common.error);
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand panel — desktop only */}
      <aside className="hidden lg:flex relative flex-col justify-between overflow-hidden bg-[hsl(var(--accent-ink))] text-background p-12">
        {/* Decorative cream wash */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-25 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(ellipse 50rem 35rem at 15% 110%, hsl(var(--accent-cream)), transparent 60%)',
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
          }}
        />

        <div className="relative flex items-center gap-3">
          <img
            src="/icons/wapmeet-logo.png"
            alt=""
            className="size-9 rounded-md object-contain ring-1 ring-white/15"
          />
          <span className="wordmark-kolhu text-3xl leading-none tracking-tight">{dv.appName}</span>
        </div>

        <div className="relative max-w-md space-y-6">
          <h1 className="font-serif text-5xl xl:text-6xl leading-[1.05] !text-background tracking-tight">
            {dv.nav.meetings}
          </h1>
        </div>

        <div className="relative flex items-end justify-between gap-6">
          <p className="mono-meta !text-white/40 max-w-xs leading-relaxed">
            ވެޕްލިފައި · {new Date().getFullYear()}
          </p>
          <svg
            aria-hidden
            viewBox="0 0 80 80"
            className="size-16 text-white/15 shrink-0"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <circle cx="40" cy="40" r="35" />
            <circle cx="40" cy="40" r="28" />
            <path d="M22 42 l10 10 l26 -26" strokeWidth="2.5" />
          </svg>
        </div>
      </aside>

      {/* Form panel */}
      <section className="flex items-center justify-center px-4 py-10 sm:py-16">
        <div className="w-full max-w-sm space-y-8">
          {/* Compact brand on mobile */}
          <div className="lg:hidden flex items-center justify-center gap-2.5">
            <img
              src="/icons/wapmeet-logo.png"
              alt=""
              className="size-8 rounded-md object-contain ring-1 ring-border"
            />
            <span className="wordmark-kolhu text-2xl leading-none text-ink tracking-tight">{dv.appName}</span>
          </div>

          <header className="space-y-2 text-center lg:text-start">
            <h2 className="font-serif text-4xl leading-none text-ink tracking-tight">
              {dv.auth.login}
            </h2>
          </header>

          <form className="space-y-5" onSubmit={submit}>
            <div className="space-y-1.5">
              <label htmlFor="email" className="field-label">
                {dv.auth.email}
              </label>
              <input
                id="email"
                type="email"
                dir="ltr"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="field-label">
                  {dv.auth.password}
                </label>
                <a
                  href="https://office.wapplify.app/forgot-password"
                  className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
                >
                  {dv.auth.forgotPassword}
                </a>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPw ? 'text' : 'password'}
                  dir="ltr"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pe-11"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? dv.auth.hidePassword : dv.auth.showPassword}
                  className="absolute inset-y-0 end-0 inline-flex w-11 items-center justify-center text-muted-foreground hover:text-foreground"
                >
                  <Icon name={showPw ? 'visibility_off' : 'visibility'} size={20} />
                </button>
              </div>
            </div>

            {error && (
              <div
                role="alert"
                className="rounded-2xl bg-[hsl(var(--error-container))] px-3.5 py-2.5 text-sm text-destructive"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={pending}
              className="btn-primary w-full py-2.5"
            >
              {pending ? dv.common.loading : (<>{dv.auth.login} <Icon name="arrow_back" size={18} /></>)}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
